import { useModalContentToggler } from '@components'

import { Button, ButtonsGroup, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvalidateBills } from '@features/bills/hooks/useInvalidateBills'
import { useInvalidateInvoices } from '@features/invoices/hooks/useInvalidateInvoices'
import { BankMatchUiFlow, UiSource } from '@features/reconciliation/enums'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { CustomEvent } from '../../../../enums/customEvent'
import { useSegment } from '../../../../hooks'
import { dispatchCustomEvent } from '../../../../utils/customEvent'
import { notifyPaymentRegistrationError } from '../../..//paymentReconciliationModal/utils/notifyPaymentRegistrationError'
import { BillType } from '../../../bills/enums/billType'
import {
  useBankLines,
  useManualPayment,
  useMultiplePayableSubjects,
  usePaymentModal,
} from '../../../paymentReconciliationModal/contexts'
import { PaymentRegistrationSuccessState } from '../../../paymentReconciliationModal/enums/paymentRegistrationSuccessState'
import { SubjectReconciliationModalStateId } from '../../../paymentReconciliationModal/enums/SubjectReconciliationModalStateId'
import { useCreateSubjectsAndBankLinesReconciliation } from '../../../paymentReconciliationModal/hooks'
import { ReconcilableParameters } from '../../../paymentReconciliationModal/query-api'
import { getTypeFromPayableSubject } from '../../../paymentReconciliationModal/utils'
import { convertSubjectsIdsToReconciliationSubjects } from '../../../paymentReconciliationModal/utils/convertSubjectsIdsToReconciliationSubjects'
import { notifyPaymentRegistrationSuccess } from '../../../paymentReconciliationModal/utils/notifyPaymentRegistrationSuccess'
import { MultipleSubjectsButton } from '../MultipleSubjectsButton'
import { getDifferenceType } from './utils'

export const BankLinesModalFooter = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const { organization } = useUserOrganization()
  const { modalId, selectedDifferenceType, isCrossCurrency } = usePaymentModal()
  const { crossCurrencyPaymentAmount } = useManualPayment()
  const { hasBankLinesDifference, selectedBankLinesIds } = useBankLines()
  const { initialSubject, selectedSubjectsIds, selectedSubjectsAmountsSum, isCashBasis } = useMultiplePayableSubjects()
  const { setActiveState } = useModalContentToggler()
  const { invalidateBills, invalidateBill } = useInvalidateBills()
  const { invalidateInvoices, invalidateInvoice, invalidateExternalInvoices, invalidateExternalInvoice } =
    useInvalidateInvoices()
  const payableSubjectType = getTypeFromPayableSubject(initialSubject)

  const handleSuccessfulInvoiceReconciliation = () => {
    track('Invoice Payment Created (Client)', {
      /* eslint-disable @typescript-eslint/naming-convention */
      invoice_id: initialSubject?.id,
      is_auto_matched: false,
      payment_mode: 'pay and reconcile',
      /* eslint-enable @typescript-eslint/naming-convention */
    })
    /** This is for updating the paymentPane web-component, the invoice query invalidations already happens by this event
     * `!!To be removed after Reactification of Invoice Page!!`
     */
    dispatchCustomEvent(CustomEvent.InvoiceUpdated)
    invalidateInvoices()
    invalidateExternalInvoices()
    invalidateInvoice(initialSubject?.id)
    invalidateExternalInvoice(initialSubject?.id)
  }

  const handleSuccessfulBillReconciliation = () => {
    track('Bill Payment Created (Client)', {
      /* eslint-disable @typescript-eslint/naming-convention */
      bill_id: initialSubject?.id,
      is_auto_matched: false,
      payment_mode: 'pay and reconcile',
      /* eslint-enable @typescript-eslint/naming-convention */
    })
    invalidateBills()
    invalidateBill(initialSubject?.id)
  }

  const { close: closeSubjectReconciliationModal } = useModal(modalId)

  const { create: createBillsAndBankLinesReconcilation, isProcessing } = useCreateSubjectsAndBankLinesReconciliation({
    onSuccess: () => {
      payableSubjectType === BillType.Bill
        ? handleSuccessfulBillReconciliation()
        : handleSuccessfulInvoiceReconciliation()
      notifyPaymentRegistrationSuccess(PaymentRegistrationSuccessState.RegisteredAndReconciled, t)
      closeSubjectReconciliationModal()
    },
    onError: (e) => notifyPaymentRegistrationError(t, e?.message),
  })

  const organizationId = organization?.id

  const isEnterPaymentButtonDisabled = useMemo(() => {
    if (isCrossCurrency) {
      const isMissingCrossCurrencyInput = !crossCurrencyPaymentAmount || !selectedBankLinesIds?.length
      return isMissingCrossCurrencyInput
    }
    const isMissingBanklinesSelection =
      (hasBankLinesDifference && !selectedDifferenceType) || !selectedBankLinesIds?.length
    return isMissingBanklinesSelection
  }, [
    crossCurrencyPaymentAmount,
    hasBankLinesDifference,
    isCrossCurrency,
    selectedBankLinesIds?.length,
    selectedDifferenceType,
  ])

  const handleEnterPaymentButtonClick = useCallback(() => {
    if (!selectedBankLinesIds?.length || !selectedSubjectsIds?.length || !organizationId) {
      return
    }
    const reconciliationSubjects = convertSubjectsIdsToReconciliationSubjects(selectedSubjectsIds, payableSubjectType)
    const reconcilableParameters: ReconcilableParameters = {}

    reconcilableParameters.differenceType = getDifferenceType(
      selectedSubjectsAmountsSum,
      crossCurrencyPaymentAmount,
      selectedDifferenceType,
    )

    if (isCrossCurrency) {
      reconcilableParameters.expectedAmount = crossCurrencyPaymentAmount
    }

    createBillsAndBankLinesReconcilation({
      bankLinesIds: selectedBankLinesIds,
      organizationId,
      subjects: reconciliationSubjects,
      parameters: reconcilableParameters,
      uiSource: UiSource.PayAndRecon,
      uiFlow: BankMatchUiFlow.Matched,
    })
  }, [
    createBillsAndBankLinesReconcilation,
    crossCurrencyPaymentAmount,
    isCrossCurrency,
    organizationId,
    selectedBankLinesIds,
    selectedDifferenceType,
    selectedSubjectsAmountsSum,
    selectedSubjectsIds,
    payableSubjectType,
  ])

  const handleRegisterMultipleBillsButton = useCallback(() => {
    track('xxx - expenses - Bank line modal - register mutliple bills clicked')

    setActiveState(SubjectReconciliationModalStateId.MultipleSubjects)
  }, [setActiveState, track])

  return (
    <ButtonsGroup>
      {!isCashBasis && (
        <MultipleSubjectsButton onClick={handleRegisterMultipleBillsButton} payableSubjectType={payableSubjectType} />
      )}
      <Button onClick={handleEnterPaymentButtonClick} disabled={isEnterPaymentButtonDisabled} loading={isProcessing}>
        {t('bill.payment_reconcile.button.enter_payment_and_reconcile')}
      </Button>
    </ButtonsGroup>
  )
}
